/**
 * UnifiedCanvasLayout — IDE-style layout: Canvas + Chat + Inspector.
 *
 * Layout (2×2 grid):
 *   ┌──────────────────────────┬──────────────┐
 *   │ DAG Canvas               │ Chat (320px) │
 *   │                          │              │
 *   ├──────────────────────────┤              │
 *   │ Inspector (full width)   │              │
 *   │ [Code] [Data] [Schema]   │              │
 *   └──────────────────────────┴──────────────┘
 *
 * Inspector collapses to 36px hint strip when no node is selected.
 * Chat spans both rows — always visible.
 *
 * Canvas mode switching (workspaceMode: 'dag' | 'mapping'):
 *   - 'dag'     → Cell 1 shows Pipeline Canvas (DAGCanvas), Cell 2 shows NodeInspectorPanel
 *   - 'mapping' → Cell 1 shows Mapping Canvas (MappingCanvasContent), Cell 2 shows MappingContextPanel
 *   These are two SEPARATE canvas systems. Mapping may or may not be part of
 *   a pipeline workflow. See docs/CANVAS_ARCHITECTURE.md.
 */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Alert, Backdrop, Badge, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Fade, IconButton, Menu, MenuItem, Popover, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import {
  AccountTree as PipelineIcon,
  VisibilityOutlined as ShowExploreIcon,
  VisibilityOffOutlined as HideExploreIcon,
  CleaningServices as CleanUpIcon,
  DragHandle as DragHandleIcon,
  Chat as ChatIcon,
  History as HistoryIcon,
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
  FileDownload as DownloadIcon,
  FileUpload as UploadIcon,
  KeyboardArrowDown as ExpandMoreIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import DAGCanvas from './DAGCanvas';
import ChatPanel from './ChatPanel';
import MappingCanvasContent from './MappingCanvasContent';
import MappingContextPanel from './MappingContextPanel';
import { computeSuggestions } from './ContextualSuggestions';
import type { Suggestion } from './ContextualSuggestions';
import NodeInspectorPanel from './NodeInspectorPanel';
import AssemblyInspectorPanel from './AssemblyInspectorPanel';
import PipelineExecutionView from './PipelineExecutionView';
import FinalizeGateModal from './FinalizeGateModal';
import CostPreview from './CostPreview';
import GenerateMappingDialog from './GenerateMappingDialog';
import PreMappingReadinessDrawer from './readiness/PreMappingReadinessDrawer';
import ConnectorPickerDialog from './ConnectorPickerDialog';
import AddSourceDialog from './AddSourceDialog';
import ExportValidationDialog from './ExportValidationDialog';
import PipelineOutputsDrawer from './PipelineOutputsDrawer';
import PipelineNewVersionDialog from './PipelineNewVersionDialog';
import PipelinePickerDialog from './PipelinePickerDialog';
import MappingGuidePreviewModal from '../MappingGuidePreviewModal';
import type { MappingGuidePreviewResponse } from '../../services/batchApi';
import {
  useWorkspaceOperations,
  useWorkspaceSession,
  useWorkspaceStream,
  useSendWorkspaceMessage,
  useWorkspaceTables,
  useWorkspaceReady,
  useDiscardOperation,
  useSetOperationExploratory,
  useRenameOperation,
  useAutoClassifyOperations,
  useUpdateSessionMeta,
  useSessionPipelineSteps,
} from '../../hooks/useWorkspaceSession';
import type { PipelineNode } from './DAGCanvas';
import type { AddNodeType } from './utils/nodeTypeRegistry';
import CommandPalette from './CommandPalette';
import PipelineContextMenu from './PipelineContextMenu';
import { useNotifications } from '../../context/NotificationContext';
import { useProjectContext } from '../../context/ProjectContext';
import { useLayoutFrame } from '../../context/LayoutFrameContext';
import {
  TRANSFORMATIVE_TYPES,
  operationsToNodes,
  mergeWithPipelineState,
} from './utils/pipelineNodeBuilder';
import { useSourceManagement } from './hooks/useSourceManagement';
import { useMappingWorkflow } from './hooks/useMappingWorkflow';
import type { CanvasSelection } from '../../canvas/types';
import { useExportPipeline } from './hooks/useExportPipeline';
import { useNodeSelection } from './hooks/useNodeSelection';
import { usePipelineVersioning } from './hooks/usePipelineVersioning';
import { listBatchPipelineVersions, listProjectPipelines, materializePipeline, deleteGeneratedMapping, type PipelineVersion } from '../../services/workspaceApi';
import { apiService } from '../../services/api';
import { getMappingHealth } from '../../services/mappingApi';
import { fetchReadiness } from '../../services/readinessApi';
import type { ReadinessFinding, ReadinessStatus } from '../../types/readiness';
import { aggregateByRootCause } from '../../types/readiness';
import {
  effectiveBatchStatusForMapping,
  canDeleteGeneratedMapping,
  hasMappingRunStarted,
  isMappingRunActive,
  isMappingRunIncomplete,
  usesMappingEditorTab,
} from '../../utils/mappingRunStatus';
import { getSchema } from '../../services/schemaApi';
import { hasHierarchy } from '../../lib/schemaTopology';
import { parsePipelineCanvasLayout } from '../../types/canvasLayout';
import type { PipelineCanvasLayout } from '../../types/canvasLayout';

// ── Types ────────────────────────────────────────────────────────────

interface UnifiedCanvasLayoutProps {
  sessionId: string;
  batchId: string;
  batchFileName?: string;
  /** Current batch status — polled from parent for mapping node lifecycle. */
  batchStatus?: string;
  /** Number of mappings generated (from batch data). */
  batchMappingCount?: number;
  /** Target schema on the batch (set at creation or by the agent). */
  batchTargetSchemaId?: string | null;
  /** Pre-selected pipeline ID for cross-batch reuse (from query param). */
  reusePipelineId?: string;
  /** Callback to clear the reusePipeline query param after consuming it. */
  onReusePipelineConsumed?: () => void;
  /** Workspace mode: 'dag' (Pipeline Canvas) or 'mapping' (Mapping Canvas). */
  mode: 'dag' | 'mapping';
}

const WORKSPACE_NODE_HINT_STORAGE_KEY = 'workspace-node-inspector-hint-dismissed';

// ── Component ────────────────────────────────────────────────────────

const UnifiedCanvasLayout: React.FC<UnifiedCanvasLayoutProps> = ({
  mode,
  sessionId,
  batchId,
  batchFileName,
  batchStatus,
  batchMappingCount,
  batchTargetSchemaId,
  reusePipelineId: reusePipelineIdProp,
  onReusePipelineConsumed,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [resolvedStepIds, setResolvedStepIds] = useState<Set<string>>(new Set());
  const [showExploration, setShowExploration] = useState(false);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [hasUnreadActivity, setHasUnreadActivity] = useState(false);

  // ── Resizable chat panel ──
  const CHAT_MIN_WIDTH = 320;
  const CHAT_DEFAULT_WIDTH = 460;
  const CHAT_MAX_WIDTH = 700;
  const [chatWidth, setChatWidth] = useState(CHAT_DEFAULT_WIDTH);
  const chatDragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  useEffect(() => {
    const handleChatDragMove = (e: MouseEvent) => {
      if (!chatDragRef.current) return;
      // Dragging left edge: moving left increases width
      const delta = chatDragRef.current.startX - e.clientX;
      const newWidth = Math.min(CHAT_MAX_WIDTH, Math.max(CHAT_MIN_WIDTH, chatDragRef.current.startWidth + delta));
      setChatWidth(newWidth);
    };
    const handleChatDragEnd = () => {
      if (!chatDragRef.current) return;
      chatDragRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', handleChatDragMove);
    window.addEventListener('mouseup', handleChatDragEnd);
    return () => {
      window.removeEventListener('mousemove', handleChatDragMove);
      window.removeEventListener('mouseup', handleChatDragEnd);
    };
  }, []);

  const handleChatDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    chatDragRef.current = { startX: e.clientX, startWidth: chatWidth };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [chatWidth]);

  // ── Command palette & context menu ──
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const { showToast } = useNotifications();
  const [commandPaletteFilter, setCommandPaletteFilter] = useState<string | undefined>();
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(null);

  // ── Cross-batch pipeline reuse ──
  const [pipelinePickerOpen, setPipelinePickerOpen] = useState(false);
  const [selectedReusePipelineId, setSelectedReusePipelineId] = useState<string | undefined>(undefined);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [versionsAnchorEl, setVersionsAnchorEl] = useState<HTMLElement | null>(null);

  // ── Workspace readiness gate ──
  // Poll a lightweight endpoint until the data-worker workspace is rebuilt.
  // This prevents 4+ heavy polling hooks from saturating the browser's
  // 6-connection-per-domain limit during reconstruction (see CLAUDE.md).
  const { ready: workspaceReady } = useWorkspaceReady(sessionId);

  // ── Core data hooks (gated behind workspace readiness) ──
  // The SSE stream delivers real-time events and invalidates query caches,
  // so polling is only needed as a fallback when the stream is disconnected.
  const stream = useWorkspaceStream(workspaceReady ? sessionId : null);
  const sseOpts = { sseConnected: stream.connected };
  const { data: session } = useWorkspaceSession(workspaceReady ? sessionId : null, sseOpts);
  const { data: operations = [] } = useWorkspaceOperations(workspaceReady ? sessionId : null, sseOpts);
  const { data: workspaceTables = [] } = useWorkspaceTables(workspaceReady ? sessionId : null, sseOpts);
  const { data: pipelineStepsData } = useSessionPipelineSteps(workspaceReady ? sessionId : null);
  const persistedPipelineSteps = pipelineStepsData?.steps ?? [];
  const sendMessage = useSendWorkspaceMessage(workspaceReady ? sessionId : null);
  const discard = useDiscardOperation(workspaceReady ? sessionId : null);
  const setExploratory = useSetOperationExploratory(workspaceReady ? sessionId : null);
  const renameOp = useRenameOperation(workspaceReady ? sessionId : null);
  const autoClassify = useAutoClassifyOperations(workspaceReady ? sessionId : null);
  const updateMeta = useUpdateSessionMeta(workspaceReady ? sessionId : null);
  const { mappingProgressMap, clearMappingProgress, pipelineProgressMap } = useNotifications();
  const mappingProgress = batchId ? mappingProgressMap[batchId] ?? null : null;
  const pipelineProgress = batchId ? pipelineProgressMap[batchId] ?? null : null;
  const MAPPED_STATUSES = ['mapped', 'reviewing', 'approved', 'transforming', 'completed'];
  const mappingRunActive = useMemo(
    () =>
      isMappingRunActive({
        batchStatus,
        sessionMeta: session?.workspace_meta,
        progress: stream.progress,
        mappingProgress,
        agentBusy: stream.agentBusy,
      }),
    [batchStatus, session?.workspace_meta, stream.progress, mappingProgress, stream.agentBusy],
  );
  const mappingRunIncomplete = useMemo(
    () =>
      isMappingRunIncomplete({
        batchStatus,
        sessionMeta: session?.workspace_meta,
        progress: stream.progress,
        mappingProgress,
        agentBusy: stream.agentBusy,
      }),
    [batchStatus, session?.workspace_meta, stream.progress, mappingProgress, stream.agentBusy],
  );
  const mappingInProgress = mappingRunActive;
  const effectiveBatchStatus = effectiveBatchStatusForMapping(batchStatus, mappingRunIncomplete);
  const sessionMeta = session?.workspace_meta;
  const savedCanvasLayout = useMemo(
    () => parsePipelineCanvasLayout(sessionMeta?.canvas_layout),
    [sessionMeta?.canvas_layout],
  );
  const handleCanvasLayoutChange = useCallback(
    (layout: PipelineCanvasLayout) => {
      updateMeta.mutate({ canvas_layout: layout });
    },
    [updateMeta],
  );
  const { targetSchema: projectTargetSchema } = useProjectContext();
  const targetSchemaId = sessionMeta?.target_schema_id as string | undefined;
  const currentTargetSchemaIdForDialog =
    sessionMeta?.target_schema_id ?? batchTargetSchemaId ?? projectTargetSchema?.schema_id ?? null;
  const { data: targetSchema } = useQuery(
    ['target-schema', targetSchemaId],
    () => getSchema(targetSchemaId!),
    { enabled: !!targetSchemaId, staleTime: 60_000 },
  );
  const hasOutputStructure = hasHierarchy(targetSchema?.definition);
  const showMappingEditor = usesMappingEditorTab(sessionMeta);
  const showMappingGuides = Boolean(batchStatus && MAPPED_STATUSES.includes(batchStatus) && showMappingEditor);

  // ── Extracted hooks ──
  const source = useSourceManagement({ sessionId, projectId: session?.project_id, batchId, operations, sendMessage });

  const mapping = useMappingWorkflow({
    mode,
    sessionId,
    batchId,
    batchStatus,
    sessionMeta,
    updateMeta,
    sendMessage,
    mappingProgress,
    operations,
    workspaceTables,
  });

  const exportPipeline = useExportPipeline({
    batchId, sessionId, sessionMeta, operations, workspaceTables,
    materializationResult: mapping.materializationResult, updateMeta,
    pipelineSteps: persistedPipelineSteps,
  });

  const [mappingGuideLoading, setMappingGuideLoading] = useState(false);
  const [mappingMenuAnchor, setMappingMenuAnchor] = useState<null | HTMLElement>(null);
  const mappingGuideInputRef = useRef<HTMLInputElement>(null);

  // Preview modal state
  const [previewData, setPreviewData] = useState<MappingGuidePreviewResponse | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  // Keep a ref to the file input event so we can reset it on cancel
  const pendingUploadFileRef = useRef<HTMLInputElement | null>(null);
  const [deleteMappingDialogOpen, setDeleteMappingDialogOpen] = useState(false);

  const handleDownloadMappingGuide = async () => {
    if (!batchId) return;
    setMappingGuideLoading(true);
    try {
      await apiService.downloadMappingGuide(batchId);
      showToast('Success', 'Mapping guide downloaded successfully.', 'success');
    } catch (err: any) {
      showToast('Error', 'Failed to download mapping guide. Please try again.', 'error');
    } finally {
      setMappingGuideLoading(false);
    }
  };

  const handleUploadMappingGuide = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !batchId) return;

    // Keep a ref to the input so we can clear it on cancel
    pendingUploadFileRef.current = event.target;

    setPreviewData(null);
    setPreviewModalOpen(true); // Open drawer immediately to display loader inside side-drawer
    setMappingGuideLoading(true);
    try {
      const preview = await apiService.previewMappingGuide(batchId, file);
      setPreviewData(preview);
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? 'Failed to parse mapping guide. Please check the file format.';
      showToast('Error', msg, 'error');
      event.target.value = '';
      setPreviewModalOpen(false); // Close drawer on error
    } finally {
      setMappingGuideLoading(false);
    }
  };

  const handleApplyPreview = useCallback(async (preview: MappingGuidePreviewResponse) => {
    if (!batchId) return;
    setMappingGuideLoading(true);
    try {
      const result = await apiService.applyMappingGuideChanges(batchId, {
        changes: preview.changes,
        recipe_id: preview.recipe_id,
      });
      setPreviewModalOpen(false);
      setPreviewData(null);
      if (pendingUploadFileRef.current) {
        pendingUploadFileRef.current.value = '';
      }
      showToast(
        'Success',
        `Applied ${result.updated_count} mapping${result.updated_count !== 1 ? 's' : ''} from the guide.`,
        'success'
      );
      queryClient.invalidateQueries(['agent-operations', sessionId]);
      queryClient.invalidateQueries(['agent-workspace', sessionId]);
      queryClient.invalidateQueries(['mapping-health', sessionId]);
      queryClient.invalidateQueries(['mapping-canvas-data', sessionId]);
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? 'Failed to apply mapping changes.';
      showToast('Error', msg, 'error');
    } finally {
      setMappingGuideLoading(false);
    }
  }, [batchId, sessionId, queryClient, showToast]);

  const handleCancelPreview = useCallback(() => {
    setPreviewModalOpen(false);
    setPreviewData(null);
    if (pendingUploadFileRef.current) {
      pendingUploadFileRef.current.value = '';
    }
  }, []);

  const versioning = usePipelineVersioning({ sessionId });

  const { data: pipelineVersions = [] } = useQuery<PipelineVersion[]>(
    ['pipeline-versions', batchId],
    () => listBatchPipelineVersions(batchId),
    { enabled: !!batchId },
  );

  // ── Independent health query for target node badges ──
  // Fires when a mapping exists, regardless of whether the mapping editor was opened.
  // Merges with any health data from the mapping editor (which may be more recent).
  const hasMappingData = !!batchId && (mapping.mappingTargetTables?.length ?? 0) > 0;
  const { data: pipelineHealthData } = useQuery(
    ['pipeline-health', batchId],
    () => getMappingHealth(batchId),
    {
      enabled: hasMappingData,
      staleTime: 60_000, // Reuse for 60s to avoid hammering the API
      refetchOnWindowFocus: false,
    },
  );

  // Prefer mapping editor health data (fresher, includes post-preview issues),
  // fall back to the independent pipeline-level query.
  const effectiveHealthData = mapping.mappingCanvasData?.healthData ?? pipelineHealthData ?? null;

  // ── Per-target-table readiness ───────────────────────────────────
  // Single /readiness fetch for the ``target_table`` consumer, grouped
  // by table name so each TargetTableNode renders its own chip from a
  // consistent source. Stays in sync with assembly/drawer chips
  // because the endpoint already applies ``applicable_consumers``
  // universally. Re-fetches on tab focus + visibility (same staleness
  // policy as AssemblyInspectorPanel / drawer) so a mapping edit
  // reflects without a full reload.
  const [readinessByTable, setReadinessByTable] = useState<
    Map<string, { status: ReadinessStatus; count: number }>
  >(new Map());

  // Per-target-table progress lookup derived from the unified progress
  // channel. Stream → Map so DAGCanvas can pluck a row's live state with
  // a single get(name). Keyed by both the raw and lowercased table id so
  // case-skew between coordinator-seeded tasks (raw schema name) and any
  // recipe-side casing doesn't silently miss the match.
  const progressByTable = React.useMemo(() => {
    const map = new Map<string, import('../../types/progress').ProgressTask>();
    const mappingPhase = stream.progress.phases.find((p) => p.id === 'mapping');
    if (!mappingPhase) return map;
    for (const task of mappingPhase.tasks) {
      map.set(task.id, task);
      const lower = task.id.toLowerCase();
      if (lower !== task.id) map.set(lower, task);
    }
    return map;
  }, [stream.progress]);
  const refreshTargetTableReadiness = React.useCallback(() => {
    if (!sessionId) return () => {};
    let cancelled = false;
    fetchReadiness({ sessionId, consumerKind: 'target_table' })
      .then((resp) => {
        if (cancelled) return;
        // Group findings by the relational target_table name. We
        // accept either ``observed.target_table`` (preferred — set
        // by data-quality checks) or fall back to ``target_path``
        // when it's the table-level coordinate (``table.field``).
        const groups = new Map<string, ReadinessFinding[]>();
        for (const f of resp.findings || []) {
          const fromObs = ((f.observed as Record<string, unknown>) || {}).target_table;
          const tableName = (typeof fromObs === 'string' && fromObs)
            ? fromObs
            : (f.target_path?.split('.')[0] || '');
          if (!tableName) continue;
          const key = tableName.toLowerCase();
          const arr = groups.get(key) || [];
          arr.push(f);
          groups.set(key, arr);
        }
        const next = new Map<string, { status: ReadinessStatus; count: number }>();
        Array.from(groups.entries()).forEach(([k, arr]) => {
          const agg = aggregateByRootCause(arr);
          const hasBlocker = agg.some((f) => f.severity === 'blocker');
          const hasAdvisory = agg.some((f) => f.severity === 'advisory');
          next.set(k, {
            status: hasBlocker ? 'not_ready' : hasAdvisory ? 'ready_with_notes' : 'ready',
            count: agg.length,
          });
        });
        setReadinessByTable(next);
      })
      .catch(() => {
        if (cancelled) return;
        setReadinessByTable(new Map());
      });
    return () => { cancelled = true; };
  }, [sessionId]);
  useEffect(() => refreshTargetTableReadiness(), [refreshTargetTableReadiness]);
  useEffect(() => {
    const onFocus = () => refreshTargetTableReadiness();
    const onVis = () => {
      if (document.visibilityState === 'visible') refreshTargetTableReadiness();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [refreshTargetTableReadiness]);

  // Load drift report on mount for versioned pipelines (survives refresh).
  // Find the pipeline belonging to this session, and if it has a parent,
  // load its stored drift report.
  useEffect(() => {
    if (pipelineVersions.length === 0 || versioning.driftReport) return;
    const activePipeline = pipelineVersions.find((pv) => pv.session_id === sessionId);
    if (activePipeline?.parent_pipeline_id) {
      versioning.loadDriftReport(activePipeline.pipeline_id);
    }
  }, [pipelineVersions, sessionId, versioning.driftReport]);

  // Fetch project pipelines to auto-prompt reuse on fresh workspaces
  const { data: projectPipelines = [] } = useQuery(
    ['project-pipelines', session?.project_id],
    () => listProjectPipelines(session!.project_id),
    { enabled: !!session?.project_id },
  );

  // Auto-open PipelineNewVersionDialog when navigated with reusePipeline query param
  useEffect(() => {
    if (reusePipelineIdProp && session?.project_id) {
      onReusePipelineConsumed?.();
      // Virtual pipeline from session operations — materialize first
      if (reusePipelineIdProp.startsWith('session:')) {
        const sid = reusePipelineIdProp.slice('session:'.length);
        materializePipeline(sid)
          .then((pipeline) => {
            setSelectedReusePipelineId(pipeline.pipeline_id);
            setVersionDialogOpen(true);
          })
          .catch(() => {
            setSelectedReusePipelineId(reusePipelineIdProp);
            setVersionDialogOpen(true);
          });
      } else {
        setSelectedReusePipelineId(reusePipelineIdProp);
        setVersionDialogOpen(true);
      }
    }
  }, [reusePipelineIdProp, session?.project_id, onReusePipelineConsumed]);

  // Auto-prompt pipeline reuse when workspace is fresh and project has existing pipelines
  const [reuseDismissed, setReuseDismissed] = useState(false);
  const [focusNodeId, setFocusNodeId] = useState<string | null>(null);
  const hasTransformOps = operations.some(
    (op) => TRANSFORMATIVE_TYPES.has(op.operation_type) && op.status !== 'discarded',
  );
  const otherBatchPipelines = projectPipelines.filter((p) => p.batch_id !== batchId);
  const showReusePrompt = !hasTransformOps && !reuseDismissed && !reusePipelineIdProp
    && otherBatchPipelines.length > 0 && !pipelinePickerOpen && !versionDialogOpen;

  // Merge source-replacement warnings and pipeline drift warnings
  const mergedSchemaWarnings = useMemo(() => {
    if (versioning.driftWarnings.size === 0) return source.schemaWarnings;
    if (source.schemaWarnings.size === 0) return versioning.driftWarnings;
    const merged = new Map(source.schemaWarnings);
    versioning.driftWarnings.forEach((v, k) => {
      if (!merged.has(k)) merged.set(k, v);
    });
    return merged;
  }, [source.schemaWarnings, versioning.driftWarnings]);

  // Ref-based callbacks for click handlers embedded in pipeline nodes.
  // These are event handlers (called on user click, not during render),
  // so a ref avoids the circular dependency: pipelineNodes → nodeSelection → pipelineNodes.
  const nodeCallbacksRef = useRef<{
    setSelectedGroupTable: (v: any) => void;
    setSelectedSourceTable: (v: any) => void;
    setSelectedNodeId: (v: string | null) => void;
  }>({ setSelectedGroupTable: () => { }, setSelectedSourceTable: () => { }, setSelectedNodeId: () => { } });

  // Build base pipeline nodes (without pending node injection)
  const { basePipelineNodes, allOutputToInputRefs } = useMemo(() => {
    const handleTargetTableClick = (tableName: string, entry: { name: string; viewName: string; status: string; ddl?: string; error?: string }) => {
      if (mappingInProgress) return;
      nodeCallbacksRef.current.setSelectedGroupTable(entry);
      nodeCallbacksRef.current.setSelectedSourceTable(null);
      nodeCallbacksRef.current.setSelectedNodeId(`__target_${tableName}__`);
    };
    const handleSourceTableClick = (tableName: string) => {
      if (mappingInProgress) return;
      // Workspace tables use bare names (e.g. "cro_subject_tracker") while
      // pipeline nodes may use namespaced names ("cro-source::cro_subject_tracker").
      const bareName = tableName.includes('::') ? tableName.slice(tableName.indexOf('::') + 2) : tableName;
      const t = workspaceTables.find(
        (wt) => wt.layer === 'source' && (wt.name === tableName || wt.name === bareName),
      );
      const resolvedName = t?.name ?? bareName;
      const info = t
        ? { name: t.name, rowCount: t.row_count, columnCount: t.column_count }
        : { name: bareName, rowCount: 0, columnCount: 0 };
      nodeCallbacksRef.current.setSelectedSourceTable(info);
      nodeCallbacksRef.current.setSelectedGroupTable(null);
      nodeCallbacksRef.current.setSelectedNodeId(`__source_${resolvedName}__`);
    };
    const selectedOutputRefs: string[] | undefined = Array.isArray(sessionMeta?.selected_output_refs)
      ? (sessionMeta!.selected_output_refs as string[])
      : undefined;
    const { nodes: opNodes, allOutputToInputRefs: outputMap } = operationsToNodes(
      operations, workspaceTables, batchId, batchFileName, sessionMeta, showExploration,
      effectiveBatchStatus, batchMappingCount, mapping.mappingTargetTables, mapping.materializationResult,
      mapping.correctionInProgress,
      handleTargetTableClick, handleSourceTableClick, exportPipeline.exportValidationResults,
      exportPipeline.qualitySummaries,
      // Pass undefined while project sources are still loading so the node
      // builder doesn't attempt multi-origin grouping with incomplete data.
      // Once loaded, the real array (possibly empty) enables correct grouping.
      source.projectSourcesLoading ? undefined : source.projectSources,
      effectiveHealthData,
      selectedOutputRefs,
    );
    // Combine persisted pipeline steps (always available once promoted) with
    // SSE live state (takes precedence for status/row_count/metrics/output_ref).
    // Gives the canvas a view of assembly/other auto-inserted steps both before
    // the pipeline runs and across page refreshes.
    const sseStepMap = new Map(
      (stream.pipelineState?.steps ?? []).map((s) => [s.step_id, s]),
    );
    const combinedSteps: any[] = persistedPipelineSteps.length > 0
      ? persistedPipelineSteps.map((p) => {
          const live = sseStepMap.get(p.step_id);
          return {
            step_id: p.step_id,
            step_type: p.step_type,
            title: p.title,
            status: live?.status || p.status,
            step_order: p.step_order,
            input_refs: p.input_refs,
            output_ref: live?.output_ref ?? p.output_ref ?? undefined,
            actual_cost: live?.actual_cost,
            row_count: live?.row_count ?? p.row_count ?? undefined,
            metrics: live?.metrics ?? p.metrics ?? undefined,
            error: live?.error ?? p.error ?? undefined,
          };
        })
      : [...(stream.pipelineState?.steps ?? [])];

    // Synthesize an assembly step from workspace_meta when the assembly ran via
    // the on-demand /workspace/assemble endpoint (stores results in
    // workspace_meta.assembly_* rather than creating a pipeline step).
    const metaAssemblyRef = sessionMeta?.assembly_output_ref as string | undefined;
    if (metaAssemblyRef?.startsWith('assembly://')
        && !combinedSteps.some((s: any) => s.step_type === 'hierarchical_assembly' && s.output_ref === metaAssemblyRef)) {
      // Anchor the assembly node downstream of the mapping collector so dagre
      // places it between the Pipeline Output / mapping node and the target
      // tables.  Prefer pipeline_output → mapping_output → schema_map
      // outputRefs in that order; fall back to the topologically last
      // opNode.outputRef.
      const findByType = (t: string) => opNodes.find((n) => n.stepType === t && n.outputRef)?.outputRef;
      const anchorRef =
        findByType('pipeline_output') ||
        findByType('mapping_output') ||
        findByType('schema_map') ||
        opNodes.slice().reverse().find((n) => n.outputRef)?.outputRef;
      // For hierarchical XML, leaf-body row counts are typically 0
      // (attribute-only elements) — surface ``instance_count`` (total
      // emitted element instances across all levels) as the meaningful
      // "size" metric instead. Tabular outputs continue to use
      // ``row_count``.
      const assemblyFormat = sessionMeta?.assembly_format;
      const isHierarchicalFormat = assemblyFormat === 'xml' || assemblyFormat === 'json';
      const displayCount = isHierarchicalFormat
        ? (sessionMeta?.assembly_instance_count ?? sessionMeta?.assembly_row_count)
        : sessionMeta?.assembly_row_count;
      combinedSteps.push({
        step_id: `__meta_assembly__${metaAssemblyRef}`,
        step_type: 'hierarchical_assembly',
        title: 'Hierarchical Assembly',
        status: 'completed',
        step_order: 9999,
        input_refs: anchorRef ? [anchorRef] : [],
        output_ref: metaAssemblyRef,
        row_count: displayCount,
        metrics: {
          output_format: assemblyFormat,
          byte_size: sessionMeta?.assembly_byte_size,
          instance_count: sessionMeta?.assembly_instance_count,
        },
      });
    }

    const pipelineStateForMerge = combinedSteps.length > 0
      ? { steps: combinedSteps }
      : null;
    const merged = mergeWithPipelineState(opNodes, pipelineStateForMerge as any);

    // Attach inline download callbacks to assembly nodes so the Download chip
    // on the AssemblyNode card fires without opening any panel.
    const enriched = merged.map((n) => {
      if (n.stepType === 'hierarchical_assembly' && n.outputRef?.startsWith('assembly://')) {
        const ref = n.outputRef;
        return {
          ...n,
          _onAssemblyDownload: () => {
            import('../../services/workspaceApi')
              .then((m) => m.downloadAssemblyOutput(sessionId, ref))
              .catch((err) => {
                showToast('Download failed', err?.message || 'Failed to download assembly output', 'error');
              });
          },
        };
      }
      return n;
    });
    return { basePipelineNodes: enriched, allOutputToInputRefs: outputMap };
  }, [operations, workspaceTables, stream.pipelineState, persistedPipelineSteps, batchId, batchFileName, sessionMeta, showExploration, effectiveBatchStatus, batchMappingCount, mapping.mappingTargetTables, mapping.materializationResult, mapping.correctionInProgress, exportPipeline.exportValidationResults, exportPipeline.qualitySummaries, source.projectSources, source.projectSourcesLoading, effectiveHealthData, mappingInProgress]);

  const nodeSelection = useNodeSelection({
    pipelineNodes: basePipelineNodes, operations, workspaceTables,
    workspaceMode: mode,
    completedOutputs: exportPipeline.completedOutputs,
    onOpenOutputsDrawer: exportPipeline.handleViewAllOutputs,
    sessionId,
    isBusy: !!stream.activeTool || sendMessage.isLoading || stream.agentBusy,
  });

  useEffect(() => {
    if (nodeSelection.askWorkspaceMessage) {
      setChatCollapsed(false);
    }
  }, [nodeSelection.askWorkspaceMessage]);

  const { setFrameVisibility, resetFrameVisibility, canvasToolbarHidden } = useLayoutFrame();

  useEffect(() => {
    if (!nodeSelection.inspectorFullscreen) return;
    setFrameVisibility({
      appBarHidden: true,
      secondaryNavHidden: true,
      canvasToolbarHidden: true,
    });
    return () => {
      resetFrameVisibility();
    };
  }, [nodeSelection.inspectorFullscreen, setFrameVisibility, resetFrameVisibility]);

  useEffect(() => {
    if (!nodeSelection.inspectorFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      nodeSelection.exitInspectorFullscreen();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [nodeSelection.inspectorFullscreen, nodeSelection.exitInspectorFullscreen]);

  const [showWorkspaceNodeHint, setShowWorkspaceNodeHint] = useState(() => {
    try {
      return !window.sessionStorage.getItem(WORKSPACE_NODE_HINT_STORAGE_KEY);
    } catch {
      return true;
    }
  });
  const showBottomRow = mapping.workspaceMode === 'mapping' || nodeSelection.inspectorOpen;
  const bottomRowHeight = mapping.workspaceMode === 'mapping' ? 36 : nodeSelection.inspectorHeight;
  const showDagHintOverlay = mapping.workspaceMode === 'dag'
    && !nodeSelection.selectedNode
    && showWorkspaceNodeHint;

  useEffect(() => {
    if (mapping.workspaceMode !== 'dag' || !nodeSelection.selectedNode || !showWorkspaceNodeHint) return;
    try {
      window.sessionStorage.setItem(WORKSPACE_NODE_HINT_STORAGE_KEY, '1');
    } catch {
      // ignore when sessionStorage is unavailable
    }
    setShowWorkspaceNodeHint(false);
  }, [mapping.workspaceMode, nodeSelection.selectedNode, showWorkspaceNodeHint]);

  // Keep ref in sync with actual setters
  nodeCallbacksRef.current = {
    setSelectedGroupTable: nodeSelection.setSelectedGroupTable,
    setSelectedSourceTable: nodeSelection.setSelectedSourceTable,
    setSelectedNodeId: nodeSelection.setSelectedNodeId,
  };

  const deleteGeneratedMappingMutation = useMutation(
    () => deleteGeneratedMapping(sessionId),
    {
      onSuccess: () => {
        setDeleteMappingDialogOpen(false);
        if (batchId) {
          clearMappingProgress(batchId);
        }
        showToast(
          'Success',
          'Mapping deleted. You can generate a new mapping when ready.',
          'success',
        );

        mapping.resetMappingState();
        if (mode === 'mapping') {
          mapping.handleBackToDag();
        }
        nodeSelection.handleDeselectNode();
        setReadinessByTable(new Map());

        const workspaceQueries = [
          ['agent-operations', sessionId],
          ['agent-workspace', sessionId],
          ['agent-session', sessionId],
        ] as const;
        for (const queryKey of workspaceQueries) {
          queryClient.invalidateQueries(queryKey);
        }
        if (batchId) {
          const batchQueries = [
            ['mappings', batchId],
            ['mappings-for-dag', batchId],
            ['mapping-health', batchId],
            ['mapping-canvas-data', sessionId],
            ['pipeline-health', batchId],
            ['batch', batchId],
          ] as const;
          for (const queryKey of batchQueries) {
            queryClient.invalidateQueries(queryKey);
          }
        }

        void queryClient.refetchQueries(['agent-operations', sessionId]);
        void queryClient.refetchQueries(['agent-workspace', sessionId]);
        void queryClient.refetchQueries(['agent-session', sessionId]);
        if (batchId) {
          void queryClient.refetchQueries(['batch', batchId]);
        }
      },
      onError: (err: unknown) => {
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
          ?? 'Failed to delete mapping. Please try again.';
        showToast('Error', String(msg), 'error');
      },
    },
  );

  // ── review_data deep-link receiver ──────────────────────────────
  // A finding whose primary_action is "review_data" routes the user
  // to ``/workspace?focus_table=X`` via the same shared
  // ``useReadinessDeepLink`` hook the mapping canvas uses. The
  // workspace's responsibility is to select the matching source
  // table (or fall through to the target table if no source by that
  // name exists) so the inspector panel scopes immediately. Without
  // this receiver the button click was a no-op and looked dead.
  //
  // Mirror of the mapping-canvas receiver's gate-reset pattern:
  // when the URL no longer carries ``focus_table``, the ref resets
  // so a *subsequent* click while already on /workspace re-fires.
  const [wsSearchParams, setWsSearchParams] = useSearchParams();
  const wsDeepLinkAppliedRef = useRef(false);
  useEffect(() => {
    const focusTable = wsSearchParams.get('focus_table');
    if (!focusTable) {
      wsDeepLinkAppliedRef.current = false;
      return;
    }
    if (wsDeepLinkAppliedRef.current) return;
    // workspaceTables loads async after mount — bail without setting
    // the gate or clearing the URL until at least one table is
    // present, then re-run when ``workspaceTables`` changes. Without
    // this the receiver would fire on the very first effect tick
    // (tables = []), miss, and strip the URL before the data
    // arrived.
    if (workspaceTables.length === 0) return;
    const bare = focusTable.includes('::')
      ? focusTable.slice(focusTable.indexOf('::') + 2)
      : focusTable;
    const lower = bare.toLowerCase();
    // Prefer source — review_data findings name source-side tables
    // ("12 rows have no matching subject"). Fall through to target
    // when no source matches (e.g. when the same payload is reused
    // for fix_in_mapping flows).
    const sourceMatch = workspaceTables.find(
      (wt) =>
        wt.layer === 'source' &&
        (wt.name.toLowerCase() === lower
          || wt.name.toLowerCase().endsWith(`::${lower}`)),
    );
    // Only flip the gate once we actually have a match to apply,
    // so a slow-loading match doesn't get silently dropped on the
    // first tick.
    wsDeepLinkAppliedRef.current = true;
    if (sourceMatch) {
      nodeCallbacksRef.current.setSelectedSourceTable({
        name: sourceMatch.name,
        rowCount: sourceMatch.row_count,
        columnCount: sourceMatch.column_count,
      });
      nodeCallbacksRef.current.setSelectedGroupTable(null);
      nodeCallbacksRef.current.setSelectedNodeId(`__source_${sourceMatch.name}__`);
    } else {
      const targetMatch = workspaceTables.find(
        (wt) => wt.layer === 'target' && wt.name.toLowerCase() === lower,
      );
      if (targetMatch) {
        nodeCallbacksRef.current.setSelectedGroupTable({
          name: targetMatch.name,
          viewName: (targetMatch as { view_name?: string }).view_name || targetMatch.name,
          status: 'completed',
        });
        nodeCallbacksRef.current.setSelectedSourceTable(null);
        nodeCallbacksRef.current.setSelectedNodeId(`__target_${targetMatch.name}__`);
      }
    }
    // Strip focus_table from the URL once consumed; preserve
    // ``finding`` / ``action`` as breadcrumb context (same as the
    // mapping-canvas receiver does).
    const next = new URLSearchParams(wsSearchParams);
    next.delete('focus_table');
    next.delete('focus_field');
    next.delete('focus_level');
    setWsSearchParams(next, { replace: true });
  }, [wsSearchParams, workspaceTables, setWsSearchParams]);

  // Final pipeline nodes: inject pending placeholder if user is creating a new transform
  const pipelineNodes = useMemo(() => {
    if (!nodeSelection.pendingNode) return basePipelineNodes;
    const merged = [...basePipelineNodes];
    const lastTransform = [...merged].reverse().find(
      (n) => TRANSFORMATIVE_TYPES.has(n.stepType) || n.stepType === 'pipeline_output',
    );
    const insertIdx = lastTransform ? merged.indexOf(lastTransform) + 1 : merged.length;
    const placeholderWithEdge: PipelineNode = {
      ...nodeSelection.pendingNode,
      inputRefs: lastTransform?.outputRef ? [lastTransform.outputRef] : [],
    };
    merged.splice(insertIdx, 0, placeholderWithEdge);
    return merged;
  }, [basePipelineNodes, nodeSelection.pendingNode]);

  // ── Chat collapse tracking ──
  useEffect(() => {
    if (chatCollapsed && (stream.activeTool || sendMessage.isLoading || stream.agentBusy)) {
      setHasUnreadActivity(true);
    }
  }, [chatCollapsed, stream.activeTool, sendMessage.isLoading, stream.agentBusy]);

  useEffect(() => {
    if (!chatCollapsed) setHasUnreadActivity(false);
  }, [chatCollapsed]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;

      // Skip if focus is inside a text input / Monaco editor
      const tag = (document.activeElement as HTMLElement)?.tagName;
      const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' ||
        (document.activeElement as HTMLElement)?.closest?.('.monaco-editor');

      if (mod && e.key === 'j') {
        e.preventDefault();
        setChatCollapsed((prev) => !prev);
      }

      // Command palette: Cmd+K (always), Cmd+T (alias, pre-filtered to transforms)
      if (mod && e.key === 'k' && !isEditable) {
        e.preventDefault();
        setCommandPaletteFilter(undefined);
        setCommandPaletteOpen(true);
      }
      if (mod && e.key === 't' && !isEditable) {
        e.preventDefault();
        setCommandPaletteFilter('transform');
        setCommandPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ── Simple callbacks (1-3 lines each) ──
  const handleMappingSelectionChange = useCallback(
    (sel: CanvasSelection | null) => {
      mapping.handleMappingSelectionChange(sel);
      if (sel) setChatCollapsed(false);
    },
    [mapping.handleMappingSelectionChange],
  );

  const handleDiscardNode = useCallback(
    (stepId: string) => discard.mutate(stepId),
    [discard],
  );

  const handleMarkExploratory = useCallback(
    (stepId: string, exploratory: boolean) => setExploratory.mutate({ operationId: stepId, exploratory }),
    [setExploratory],
  );

  const handleRenameNode = useCallback(
    (stepId: string, label: string) => renameOp.mutate({ operationId: stepId, label }),
    [renameOp],
  );

  const phase = useMemo(() => {
    if (stream.pipelineState?.status === 'running') return 'executing' as const;
    if (stream.pipelineState?.status === 'paused') return 'review_required' as const;
    return 'building' as const;
  }, [stream.pipelineState]);

  const handlePipelineRerun = useCallback(
    (_pipelineId: string) => sendMessage.mutate('Re-run the entire pipeline from the beginning'),
    [sendMessage],
  );
  const handlePipelineResume = useCallback(() => sendMessage.mutate('Resume the pipeline from where it failed'), [sendMessage]);
  const handleStepRetry = useCallback(
    (stepId: string, stepTitle: string) => sendMessage.mutate(`Retry the failed step "${stepTitle}" (step_id: ${stepId})`),
    [sendMessage],
  );
  const handleStepFix = useCallback((stepId: string) => nodeSelection.setSelectedNodeId(stepId), []);
  const handleFocusNode = useCallback((operationId: string) => {
    nodeSelection.setSelectedNodeId(operationId);
    setFocusNodeId(operationId);
  }, [nodeSelection]);
  const handleFocusComplete = useCallback(() => setFocusNodeId(null), []);
  const handleCostApprove = useCallback(() => sendMessage.mutate('approve_cost'), [sendMessage]);
  const handleCostReject = useCallback(() => sendMessage.mutate('reject_cost'), [sendMessage]);
  const handleResolveIssue = useCallback(
    (stepId: string, action: 'auto_fix' | 'skip' | 'retry') => {
      sendMessage.mutate(`resolve_gate:${stepId}:${action}`);
      setResolvedStepIds((prev) => new Set(prev).add(stepId));
    },
    [sendMessage],
  );
  const handleFinalize = useCallback(() => {
    sendMessage.mutate('finalize_pipeline');
    setResolvedStepIds(new Set());
  }, [sendMessage]);

  // ── Computed values ──
  const executionOverlay = stream.pipelineState ? (
    <PipelineExecutionView
      pipeline={stream.pipelineState}
      stepProgress={stream.pipelineState.stepProgress}
      onRerun={handlePipelineRerun}
      onStepRetry={handleStepRetry}
      onStepFix={handleStepFix}
      onResume={handlePipelineResume}
    />
  ) : null;

  const phaseDetail = useMemo(() => {
    const nonSourceCount = pipelineNodes.filter(
      (n) => n.stepType !== 'source_group' && n.stepType !== 'mapping_output',
    ).length;
    if (nonSourceCount <= 0) return undefined;
    const totalOps = operations.filter(
      (op) => TRANSFORMATIVE_TYPES.has(op.operation_type) && op.status !== 'discarded',
    ).length;
    if (totalOps > nonSourceCount) {
      return `${nonSourceCount} pipeline step${nonSourceCount !== 1 ? 's' : ''} (${totalOps} total)`;
    }
    return `${nonSourceCount} step${nonSourceCount !== 1 ? 's' : ''}`;
  }, [pipelineNodes, operations]);

  const contextualSuggestions: Suggestion[] = useMemo(() => {
    const sourceCount = pipelineNodes.filter((n) => n.stepType === 'source' || n.stepType === 'source_group').length;
    const hasTransforms = pipelineNodes.some((n) => TRANSFORMATIVE_TYPES.has(n.stepType));
    const isMappingInProgress = mappingRunActive;

    return computeSuggestions({
      sourceCount,
      hasTransforms,
      batchStatus: effectiveBatchStatus,
      mappingCount: batchMappingCount,
      lowConfidenceCount: 0, // TODO: wire from mapping health API
      inMappingMode: mode === 'mapping',
      mappingInProgress: isMappingInProgress,
      mappingComplete: hasMappingRunStarted(sessionMeta),
      onSwitchToMappingMode: mapping.handleOpenMappingEditor,
      onOpenGenerateMapping: mapping.handleOpenGenerateMapping,
    });
  }, [pipelineNodes, effectiveBatchStatus, batchMappingCount, mode, sessionMeta, mappingRunActive, mapping.handleOpenMappingEditor, mapping.handleOpenGenerateMapping])
    // Filter out mapping suggestions — "Generate Mapping" is now in the toolbar
    .filter((s) => s.id !== 'map-schema' && s.id !== 'generate-mapping');

  const isBusy = mappingInProgress;
  // Agent-driven mapping (chat / iterative) stays conversational — only engine runs lock chat.
  const chatMappingBusy = showMappingEditor && mappingInProgress;
  const outputsEnabled = Boolean(
    sessionMeta?.mapping_complete
    || (batchStatus && ['mapped', 'approved', 'completed'].includes(batchStatus))
    || operations.some((op: any) => TRANSFORMATIVE_TYPES.has(op.operation_type) && ['executed', 'promoted', 'corrected', 'completed'].includes(op.status)),
  );
  const isGenerateMappingDisabled = isBusy;
  const generateMappingTooltip = isBusy
    ? 'Mapping is currently in progress. Please wait for it to finish.'
    : 'Generate schema mapping from source to target';
  const showDeleteMapping = canDeleteGeneratedMapping({
    batchStatus,
    sessionMeta,
    batchMappingCount,
    mappingTargetTables: mapping.mappingTargetTables,
    progress: stream.progress,
    mappingProgress,
    agentBusy: stream.agentBusy,
  });

  // ── Workspace rebuilding gate ──
  if (!workspaceReady) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 2,
        }}
      >
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          Rebuilding workspace…
        </Typography>
      </Box>
    );
  }

  // ── Render ──
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Top bar */}
      {mode === 'dag' && !canvasToolbarHidden && (
        <Box
          sx={{
            px: 2, py: 0.75, borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 40,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <PipelineIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="subtitle2" fontWeight={600}>Pipeline Canvas</Typography>
            {phaseDetail && (
              <Typography variant="caption" color="text.secondary">· {phaseDetail}</Typography>
            )}
            <Tooltip title={showExploration ? 'Hide exploration steps' : 'Show exploration steps'}>
              <IconButton
                size="small"
                onClick={() => setShowExploration((v) => !v)}
                sx={{ ml: 0.5, p: 0.5, color: showExploration ? 'primary.main' : 'text.disabled' }}
              >
                {showExploration ? <ShowExploreIcon sx={{ fontSize: 16 }} /> : <HideExploreIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
            {operations.length > 3 && (
              <Tooltip title="Auto-classify exploration operations to clean up the pipeline">
                <Button
                  size="small" variant="text"
                  startIcon={<CleanUpIcon sx={{ fontSize: 14 }} />}
                  onClick={() => autoClassify.mutate()}
                  disabled={autoClassify.isLoading}
                  sx={{ fontSize: 11, textTransform: 'none', ml: 0.5 }}
                >
                  Clean Up
                </Button>
              </Tooltip>
            )}
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {mode === 'dag' && (
              <Stack direction="row" spacing={0.5} sx={{ '& .MuiButton-root': { fontSize: 11, textTransform: 'none', borderRadius: 1.5, px: 1.5, py: 0.25, minHeight: 28, borderColor: 'divider', color: 'text.primary', '&:hover': { bgcolor: 'action.hover', borderColor: 'text.secondary' } } }}>
                {showDeleteMapping && (
                  <Tooltip title="Delete the generated mapping, recipe, and mapped views">
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                      onClick={() => setDeleteMappingDialogOpen(true)}
                      disabled={isBusy || deleteGeneratedMappingMutation.isLoading}
                      sx={{
                        '&.Mui-disabled': {
                          cursor: 'not-allowed',
                          pointerEvents: 'auto',
                        },
                      }}
                    >
                      Delete Mapping
                    </Button>
                  </Tooltip>
                )}
                <Tooltip title={`Add transform (${navigator.platform?.includes('Mac') ? '⌘' : 'Ctrl+'}K)`}>
                  <Button size="small" variant="outlined" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                    onClick={() => { setCommandPaletteFilter(undefined); setCommandPaletteOpen(true); }}
                    disabled={isBusy}
                    sx={{
                      '&.Mui-disabled': {
                        cursor: 'not-allowed',
                        pointerEvents: 'auto',
                      },
                    }}>
                    Add Transform
                  </Button>
                </Tooltip>
                {mapping.handleOpenGenerateMapping && (
                  <Tooltip title={generateMappingTooltip}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={mapping.handleOpenGenerateMapping}
                      disabled={isGenerateMappingDisabled}
                      sx={{
                        '&.Mui-disabled': {
                          cursor: 'not-allowed',
                          pointerEvents: 'auto',
                        },
                      }}
                    >
                      Generate Mapping
                    </Button>
                  </Tooltip>
                )}
                {showMappingGuides && !mappingInProgress && (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
                      onClick={(e) => setMappingMenuAnchor(e.currentTarget)}
                      disabled={mappingGuideLoading}
                      sx={{ minWidth: 140 }}
                    >
                      Mapping Guide
                    </Button>
                    <Menu
                      anchorEl={mappingMenuAnchor}
                      open={Boolean(mappingMenuAnchor)}
                      onClose={() => setMappingMenuAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      PaperProps={{
                        sx: {
                          mt: 0.5,
                          minWidth: 180,
                          boxShadow: '0px 4px 20px rgba(0,0,0,0.1)',
                          border: '1px solid',
                          borderColor: 'divider',
                        }
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          setMappingMenuAnchor(null);
                          handleDownloadMappingGuide();
                        }}
                        disabled={!sessionMeta?.mapping_complete}
                        sx={{ fontSize: 12, py: 1 }}
                      >
                        <DownloadIcon sx={{ fontSize: 16, mr: 1.5, color: 'text.secondary' }} />
                        Download Mapping Guide
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setMappingMenuAnchor(null);
                          mappingGuideInputRef.current?.click();
                        }}
                        disabled={mappingGuideLoading}
                        sx={{ fontSize: 12, py: 1 }}
                      >
                        <UploadIcon sx={{ fontSize: 16, mr: 1.5, color: 'text.secondary' }} />
                        {mappingGuideLoading ? 'Uploading...' : 'Upload Mapping Guide'}
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Stack>
            )}
            {pipelineVersions.length > 0 && (
              <Tooltip title="Pipeline version history">
                <Button
                  size="small"
                  variant="text"
                  startIcon={<HistoryIcon sx={{ fontSize: 14 }} />}
                  onClick={(e) => setVersionsAnchorEl(e.currentTarget)}
                  sx={{ fontSize: 11, textTransform: 'none' }}
                >
                  v{pipelineVersions.length}
                </Button>
              </Tooltip>
            )}
            {session?.project_id && (
              <Tooltip title="Apply a pipeline from another batch to this data">
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setPipelinePickerOpen(true)}
                  disabled={isBusy}
                  sx={{
                    fontSize: 11,
                    textTransform: 'none',
                    '&.Mui-disabled': {
                      cursor: 'not-allowed',
                      pointerEvents: 'auto',
                    },
                  }}
                >
                  Reuse Pipeline
                </Button>
              </Tooltip>
            )}
          </Stack>
        </Box>
      )}

      {/* Source replacement alert banner */}
      {source.sourceReplacementAlert && (
        <Alert
          severity={source.sourceReplacementAlert.brokenCount > 0 ? 'error' : 'warning'}
          onClose={source.dismissReplacementAlert}
          sx={{ mx: 2, mt: 1, fontSize: 12 }}
          action={
            <Button
              color="inherit"
              size="small"
              sx={{ fontSize: 11, textTransform: 'none' }}
              onClick={() => {
                // Ask agent to re-execute affected operations
                const ids = [
                  ...source.sourceReplacementAlert!.brokenOperationIds,
                  ...source.sourceReplacementAlert!.affectedOperationIds,
                ];
                sendMessage.mutate(
                  `Source "${source.sourceReplacementAlert!.tableName}" was replaced. Please re-execute the affected transforms: ${ids.join(', ')}`,
                );
                source.dismissReplacementAlert();
              }}
            >
              Re-execute All
            </Button>
          }
        >
          Source "{source.sourceReplacementAlert.tableName}" changed
          {source.sourceReplacementAlert.brokenCount > 0 &&
            ` — ${source.sourceReplacementAlert.brokenCount} broken`}
          {source.sourceReplacementAlert.affectedCount > 0 &&
            ` — ${source.sourceReplacementAlert.affectedCount} affected`}
          {' '}transform{(source.sourceReplacementAlert.brokenCount + source.sourceReplacementAlert.affectedCount) !== 1 ? 's' : ''}
        </Alert>
      )}

      {/* New sources profiled alert — only shown when mapping is already complete */}
      {source.newSourcesProfiledAlert && sessionMeta?.mapping_complete && (
        <Alert
          severity="info"
          onClose={source.dismissNewSourcesProfiledAlert}
          sx={{ mx: 2, mt: 1, fontSize: 12 }}
          action={
            <Stack direction="row" spacing={0.5}>
              <Button
                color="inherit"
                size="small"
                sx={{ fontSize: 11, textTransform: 'none' }}
                onClick={() => {
                  source.handleTriggerRemap();
                }}
              >
                Update Mapping
              </Button>
              <Button
                color="inherit"
                size="small"
                sx={{ fontSize: 11, textTransform: 'none' }}
                onClick={source.dismissNewSourcesProfiledAlert}
              >
                Dismiss
              </Button>
            </Stack>
          }
        >
          {source.newSourcesProfiledAlert.sourceIds.length} new source{source.newSourcesProfiledAlert.sourceIds.length !== 1 ? 's' : ''} profiled. Update mapping to include them?
        </Alert>
      )}

      {/* Auto-prompt: reuse pipeline from another batch */}
      {showReusePrompt && (
        <Alert
          severity="info"
          onClose={() => setReuseDismissed(true)}
          sx={{ mx: 2, mt: 1, fontSize: 12 }}
          action={
            <Button
              color="inherit"
              size="small"
              sx={{ fontSize: 11, textTransform: 'none' }}
              onClick={() => setPipelinePickerOpen(true)}
            >
              Choose Pipeline
            </Button>
          }
        >
          {otherBatchPipelines.length === 1
            ? `A pipeline from "${otherBatchPipelines[0].batch_name || 'another batch'}" exists in this project. Reuse it?`
            : `${otherBatchPipelines.length} pipelines exist in this project. Want to reuse one?`}
        </Alert>
      )}

      {/* Table selection filter chips — shown when user selected a subset of tables for mapping */}
      {sessionMeta?.mapping_complete && (sessionMeta?.source_tables || sessionMeta?.target_tables) && (
        <Stack direction="row" spacing={1} sx={{ mx: 2, mt: 0.5 }}>
          {sessionMeta.source_tables && (() => {
            const totalSources = workspaceTables.filter((t) => t.layer === 'source').length;
            const selectedCount = sessionMeta.source_tables.length;
            return totalSources > 0 && selectedCount < totalSources ? (
              <Chip
                label={`Mapping ${selectedCount} of ${totalSources} source tables`}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontSize: 11, height: 22 }}
                onDelete={() => {
                  // Clear source table filter — next mapping will use all sources
                  updateMeta.mutate({ source_tables: null });
                }}
              />
            ) : null;
          })()}
          {sessionMeta.target_tables && (() => {
            const targetTotal = mapping.mappingTargetTables?.length ?? sessionMeta.target_tables.length;
            const selectedCount = sessionMeta.target_tables.length;
            return targetTotal > 0 && selectedCount < targetTotal ? (
              <Chip
                label={`Mapping ${selectedCount} of ${targetTotal} target tables`}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontSize: 11, height: 22 }}
                onDelete={() => {
                  updateMeta.mutate({ target_tables: null });
                }}
              />
            ) : null;
          })()}
        </Stack>
      )}

      {/* Main content: 2×2 grid */}
      <Box
        sx={{
          flex: 1,
          display: 'grid',
          // Keep chat at a stable width so the inspector can resize vertically
          // without collapsing sidebar content.
          gridTemplateColumns: chatCollapsed ? '1fr 48px' : `1fr ${chatWidth}px`,
          gridTemplateRows: showBottomRow
            ? `1fr ${bottomRowHeight}px`
            : '1fr',
          overflow: 'hidden', minHeight: 0,
          transition: 'grid-template-columns 0.2s ease',
        }}
      >
        {/* Cell 1: DAG Canvas or Mapping Canvas */}
        <Box sx={{ gridRow: 1, gridColumn: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
          {mapping.workspaceMode === 'dag' ? ( 
            <>
              <DAGCanvas
                pipelineNodes={pipelineNodes}
                selectedNodeId={nodeSelection.selectedNodeId}
                onNodeClick={isBusy ? () => { } : nodeSelection.handleNodeClick}
                onPaneClick={nodeSelection.handlePaneClick}
                phase={phase}
                phaseDetail={phaseDetail}
                executionOverlay={executionOverlay}
                onDiscardNode={handleDiscardNode}
                onMarkExploratory={handleMarkExploratory}
                onRenameNode={handleRenameNode}
                allOutputToInputRefs={allOutputToInputRefs}
                onNavigate={navigate}
                onOpenEditor={showMappingEditor ? mapping.handleOpenMappingEditor : undefined}
                onViewAllOutputs={exportPipeline.handleViewAllOutputs}
                outputsEnabled={outputsEnabled}
                outputCount={exportPipeline.completedOutputs.length}
                onExport={isBusy ? undefined : exportPipeline.handleExport}
                onAddTransform={nodeSelection.handleAddTransform}
                onAddNode={nodeSelection.handleAddNode}
                isBusy={isBusy}
                hasPendingNode={!!nodeSelection.pendingNode}
                onChangeSchema={mapping.handleOpenGenerateMapping}
                onStartExport={isBusy ? undefined : exportPipeline.handleStartExport}
                onAddSource={source.handleOpenAddSourceDialogFiles}
                onAddFromConnector={source.handleOpenAddSourceDialogConnector}
                onReplaceSource={source.handleReplaceSource}
                schemaWarnings={mergedSchemaWarnings}
                focusNodeId={focusNodeId}
                onFocusComplete={handleFocusComplete}
                onPaneContextMenu={(e: React.MouseEvent) => {
                  e.preventDefault();
                  setContextMenuPosition({ x: e.clientX, y: e.clientY });
                }}
                readinessByTable={readinessByTable}
                progressByTable={progressByTable}
                savedCanvasLayout={savedCanvasLayout}
                onCanvasLayoutChange={handleCanvasLayoutChange}
              />
              <Fade in={showDagHintOverlay} timeout={400} unmountOnExit>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    px: 2,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.background.paper, 0.92),
                    color: 'text.secondary',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                    pointerEvents: 'none',
                    zIndex: 5,
                  }}
                >
                  <Typography variant="caption" color="inherit">
                    Click a node to inspect
                  </Typography>
                </Box>
              </Fade>
            </>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {mapping.schemaDriftWarnings.length > 0 && (
                <Alert
                  severity="warning"
                  sx={{ mx: 1, mt: 1, py: 0.5 }}
                  action={
                    <Button
                      color="inherit" size="small"
                      disabled={mappingInProgress}
                      onClick={() => {
                        const driftTables = mapping.schemaDriftWarnings.map(w => w.outputTable).join(', ');
                        sendMessage.mutate(
                          `These transform outputs are not in the current mapping: ${driftTables}. Re-generate the mapping to include these updated source tables.`,
                        );
                        mapping.handleBackToDag();
                      }}
                      sx={{ fontSize: 11, whiteSpace: 'nowrap' }}
                    >
                      Update Mapping
                    </Button>
                  }
                >
                  {mapping.schemaDriftWarnings.length === 1
                    ? `Transform "${mapping.schemaDriftWarnings[0].title}" created table "${mapping.schemaDriftWarnings[0].outputTable}" — mapping may reference outdated sources.`
                    : `${mapping.schemaDriftWarnings.length} transforms created new tables — mapping may reference outdated sources.`}
                </Alert>
              )}
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <MappingCanvasContent
                  batchId={batchId}
                  sessionId={sessionId}
                  onBack={mapping.handleBackToDag}
                  onSelectionChange={handleMappingSelectionChange}
                  onDataReady={mapping.handleMappingDataReady}
                  onMutationHandlers={mapping.handleMutationHandlersReady}
                  destinationExportStatus={sessionMeta?.destination_status}
                  targetSchemaIdOverride={sessionMeta?.target_schema_id}
                />
              </Box>
            </Box>
          )}
        </Box>

        {/* Cell 2: Chat Panel or MappingContextPanel */}
        <Box
          sx={{
            // Chat should remain fully usable while the inspector resizes.
            gridRow: showBottomRow ? '1 / 3' : 1,
            gridColumn: 2,
            borderLeft: 1,
            borderColor: 'divider',
            minHeight: 0,
            overflow: 'hidden',
            position: 'relative',
            minWidth: chatCollapsed ? 48 : CHAT_MIN_WIDTH,
            bgcolor: chatCollapsed ? 'background.paper' : '#fafafa',
            boxShadow: chatCollapsed ? 'none' : '-4px 0 12px rgba(0,0,0,0.03)',
            zIndex: 5,
          }}
        >
          {/* Drag handle for resizing chat panel */}
          {!chatCollapsed && (
            <Box
              onMouseDown={handleChatDragStart}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: 6,
                height: '100%',
                cursor: 'col-resize',
                zIndex: 10,
                '&:hover': { bgcolor: 'primary.main', opacity: 0.3 },
                ...(chatDragRef.current ? { bgcolor: 'primary.main', opacity: 0.3 } : {}),
              }}
            />
          )}
          {mode === 'mapping' && (
            <MappingContextPanel
              sessionId={sessionId}
              batchFileName={batchFileName}
              selectedNode={nodeSelection.selectedNode}
              onDeselectNode={nodeSelection.handleDeselectNode}
              operations={operations}
              stream={stream}
              pendingMessage={nodeSelection.askWorkspaceMessage}
              onPendingMessageConsumed={nodeSelection.handlePendingMessageConsumed}
              isBusy={isBusy}
              contextualSuggestions={[]}
              logicProps={{
                selection: mapping.mappingSelection,
                mappingResponse: mapping.mappingCanvasData.mappingResponse,
                onSave: mapping.mutationHandlers?.handleSave,
                onCreate: mapping.mutationHandlers?.handleCreateMapping,
                onDelete: mapping.mutationHandlers?.handleDeleteMapping,
                onValidate: mapping.mutationHandlers?.handleValidateTransformation,
                saving: mapping.mutationHandlers?.isSavingMapping,
                deleting: mapping.mutationHandlers?.isDeletingMapping,
                validating: mapping.mutationHandlers?.isValidatingMapping,
              }}
              diagnosticsProps={{
                lineageChains: mapping.mappingCanvasData.lineageChains ?? [],
                currentNode: mapping.mappingSelection?.kind === 'node'
                  ? mapping.mappingSelection.node?.data?.label ?? ''
                  : mapping.mappingSelection?.kind === 'edge'
                    ? mapping.mappingSelection.edge?.data?.label ?? ''
                    : '',
                healthData: mapping.mappingCanvasData.healthData,
                healthLoading: mapping.mappingCanvasData.healthLoading,
                manualAdjustments: mapping.mappingCanvasData.manualAdjustments ?? [],
                promptRuns: mapping.mappingCanvasData.promptRuns ?? [],
                edgeFieldMap: mapping.mappingCanvasData.edgeFieldMap ?? {},
                // Issue #656: surface the selected field's table + name so
                // the Inspector accordions (Review items / Rules / Samples)
                // know what to scope to. Edge selections carry the
                // mapping; node selections of `targetField` carry it on
                // node.data.
                selectedTable:
                  mapping.mappingSelection?.kind === 'edge'
                    ? mapping.mappingSelection.edge?.data?.metadata?.target_table as string | undefined
                    : mapping.mappingSelection?.kind === 'node'
                      ? mapping.mappingSelection.node?.data?.table
                      : undefined,
                selectedField: mapping.mappingSelection?.kind === 'edge'
                  ? mapping.mappingSelection.edge?.data?.label ?? undefined
                  : mapping.mappingSelection?.kind === 'node'
                    ? mapping.mappingSelection.node?.data?.field
                    : undefined,
                selectedTargetField: mapping.mappingSelection?.kind === 'edge'
                  ? mapping.mappingSelection.edge?.target ?? null
                  : null,
                // Issue #656: data needed by the new field-scoped Inspector
                // sections in the Diagnostics tab.
                batchId,
                mappingResponse: mapping.mappingCanvasData.mappingResponse,
              }}
              mappingActive={!!sessionMeta?.mapping_complete}
              onSwitchToDagMode={mapping.handleBackToDag}
              collapsed={chatCollapsed}
              onToggleCollapse={() => setChatCollapsed((c) => !c)}
            />
          )}
          {mode === 'dag' && (
            chatCollapsed ? (
              <Box
                sx={{
                  width: 48, height: '100%',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  pt: 1, gap: 1, bgcolor: 'background.paper',
                }}
              >
                <Tooltip title="Expand chat (⌘J)" placement="left">
                  <IconButton size="small" onClick={() => setChatCollapsed(false)} sx={{ color: 'primary.main' }}>
                    <Badge color="error" variant="dot" invisible={!hasUnreadActivity}>
                      <ChatIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              // Collapse control is now inline in ChatPanel's header (right
              // side, after the "Source Layer" chip / node-close button) so
              // it doesn't burn its own row above the panel.
              <Box sx={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
                <ChatPanel
                  sessionId={sessionId}
                  batchId={batchId}
                  batchFileName={batchFileName}
                  selectedNode={nodeSelection.selectedNode}
                  onDeselectNode={nodeSelection.handleDeselectNode}
                  operations={operations}
                  stream={stream}
                  pendingMessage={nodeSelection.askWorkspaceMessage}
                  onPendingMessageConsumed={nodeSelection.handlePendingMessageConsumed}
                  isBusy={chatMappingBusy}
                  contextualSuggestions={contextualSuggestions}
                  onOpenEditor={showMappingEditor ? mapping.handleOpenMappingEditor : undefined}
                  onOpenGenerateMapping={mapping.handleOpenGenerateMapping}
                  mappingActive={!!sessionMeta?.mapping_complete}
                  mappingMode={sessionMeta?.mapping_mode}
                  mappingTargetCount={mapping.mappingTargetTables?.length}
                  onFocusNode={handleFocusNode}
                  onCollapse={() => setChatCollapsed(true)}
                  destinationExportStatus={sessionMeta?.destination_status}
                />
              </Box>
            )
          )}
        </Box>

        {/* Cell 3: Inspector Panel */}
        {showBottomRow && (
          <Box
            sx={{
              gridRow: nodeSelection.inspectorFullscreen ? '1 / -1' : 2,
              // Restrict inspector horizontally to the canvas column so it
              // never overlaps / squeezes the chat sidebar.
              gridColumn: nodeSelection.inspectorFullscreen ? '1 / -1' : 1,
              borderTop: nodeSelection.inspectorFullscreen ? 0 : 1,
              borderColor: 'divider',
              minHeight: 0, overflow: 'hidden', position: 'relative',
              zIndex: nodeSelection.inspectorFullscreen ? 20 : 'auto',
              bgcolor: nodeSelection.inspectorFullscreen ? 'background.paper' : undefined,
            }}
          >
            {nodeSelection.inspectorOpen && !nodeSelection.inspectorFullscreen && (
              <Box
                onMouseDown={nodeSelection.handleInspectorDragStart}
                sx={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: 12,
                  cursor: 'row-resize', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 10, borderTop: '2px solid transparent', transition: 'border-color 0.15s',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    '& .drag-indicator': { opacity: 1 },
                  },
                }}
              >
                <DragHandleIcon
                  className="drag-indicator"
                  sx={{ fontSize: 14, color: 'text.disabled', opacity: 0.3, transition: 'opacity 0.15s' }}
                />
              </Box>
            )}
            {nodeSelection.selectedNode?.stepType === 'hierarchical_assembly' ? (
              <AssemblyInspectorPanel
                sessionId={sessionId}
                selectedNode={nodeSelection.selectedNode}
                onClose={nodeSelection.handleDeselectNode}
                onExportToConnector={exportPipeline.handleViewAllOutputs}
              />
            ) : (
              <NodeInspectorPanel
                selectedNode={nodeSelection.selectedNode}
                selectedOperation={nodeSelection.selectedOperation}
                sessionId={sessionId}
                batchId={batchId}
                onClose={nodeSelection.handleDeselectNode}
                pipelineExists={!!stream.pipelineState}
                onCancelPending={nodeSelection.pendingNode ? nodeSelection.handleCancelPendingNode : undefined}
                onPendingCreated={nodeSelection.pendingNode ? nodeSelection.handlePendingNodeCreated : undefined}
                pendingNodeType={nodeSelection.pendingNode?.stepType as AddNodeType | undefined}
                isBusy={isBusy}
                workspaceTables={workspaceTables}
                onAskAboutColumn={nodeSelection.handleAskAboutColumn}
                onActiveTabChange={nodeSelection.setInspectorActiveTab}
                onAskAgent={nodeSelection.handleAskAgentAboutNode}
                schemaWarning={nodeSelection.selectedNodeId ? mergedSchemaWarnings.get(nodeSelection.selectedNodeId) : undefined}
                driftResult={nodeSelection.selectedNodeId ? versioning.getStepDrift(nodeSelection.selectedNodeId) : undefined}
                onReplayStep={versioning.driftReport ? async (stepId: string) => {
                  if (versioning.driftReport) await versioning.replay(versioning.driftReport.pipeline_id, stepId);
                } : undefined}
                workspaceMode={mapping.workspaceMode}
                isFullscreen={nodeSelection.inspectorFullscreen}
                onToggleFullscreen={nodeSelection.toggleInspectorFullscreen}
                sseConnected={stream.connected}
                restoredCreationState={nodeSelection.restoredPendingSnapshot}
                onCreationStateChange={nodeSelection.updatePendingCreationState}
                healthIssues={(() => {
                  const hd = effectiveHealthData;
                  if (!hd?.issues || hd.issues.length === 0) return undefined;
                  return hd.issues.map((i: any) => ({
                    column: i.target_field,
                    message: i.message,
                    severity: i.severity,
                    remediation: i.remediation,
                  }));
                })()}
                targetSchemaSnapshot={mapping.mappingCanvasData?.mappingResponse?.metadata?.target_schema_snapshot}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Dialogs and overlays */}
      {stream.costApprovalPending && (
        <CostPreview
          open={true}
          onClose={handleCostReject}
          onApprove={handleCostApprove}
          onReject={handleCostReject}
          operationType={stream.costApprovalPending.operationType}
          description={stream.costApprovalPending.description}
          estimatedCostUsd={stream.costApprovalPending.estimatedCostUsd}
          rowCount={stream.costApprovalPending.rowCount}
          estimatedDurationSec={stream.costApprovalPending.estimatedDurationSec}
        />
      )}

      {stream.finalizationBlocked && stream.finalizationBlocked.length > 0 && (
        <FinalizeGateModal
          open={true}
          onClose={() => { }}
          issues={stream.finalizationBlocked}
          onResolve={handleResolveIssue}
          onFinalize={handleFinalize}
          resolvedStepIds={resolvedStepIds}
        />
      )}

      <GenerateMappingDialog
        open={mapping.generateMappingOpen}
        onClose={mapping.closeGenerateMappingDialog}
        batchId={batchId}
        sessionId={sessionId}
        currentSchemaId={currentTargetSchemaIdForDialog}
        onMappingEngineStart={mapping.handleMappingEngineStart}
        onIterativeStart={mapping.handleIterativeStart}
        onContinueToReadiness={mapping.handleContinueToReadiness}
        workspaceTables={workspaceTables}
      />

      <Dialog
        open={deleteMappingDialogOpen}
        onClose={() => !deleteGeneratedMappingMutation.isLoading && setDeleteMappingDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderTop: 4,
            borderColor: 'error.main',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
            fontWeight: 700,
          }}
        >
          <DeleteOutlineIcon />
          Delete Mapping?
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
            This action is permanent and cannot be undone.
          </Alert>
          <DialogContentText color="text.primary">
            The mapping will be deleted forever — including all field mappings, the recipe,
            mapped views, and canvas layout. Your target schema selection will be kept so you
            can generate a new mapping later.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteMappingDialogOpen(false)}
            disabled={deleteGeneratedMappingMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteGeneratedMappingMutation.mutate()}
            disabled={deleteGeneratedMappingMutation.isLoading}
            startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
          >
            {deleteGeneratedMappingMutation.isLoading ? 'Deleting…' : 'Delete Forever'}
          </Button>
        </DialogActions>
      </Dialog>

      {/*
        Persistent right-side readiness drawer. Renders alongside the
        workspace canvas (does NOT block interaction) so the operator
        can inspect source files / mappings while resolving findings.
        Opened from the Generate Mapping dialog's "Continue to
        Readiness" CTA; closes when the operator clicks the X or after
        Start Mapping fires (which hands control back to the host's
        engine/iterative kickoff handler).
      */}
      <PreMappingReadinessDrawer
        open={mapping.readinessDrawer !== null}
        batchId={batchId}
        sessionId={sessionId}
        schemaId={mapping.readinessDrawer?.schema.schema_id || ''}
        schemaName={mapping.readinessDrawer?.schema.schema_name}
        onClose={mapping.closeReadinessDrawer}
        onReadyToStart={mapping.handleReadinessStart}
      />

      <ConnectorPickerDialog
        open={exportPipeline.connectorPickerOpen}
        onClose={exportPipeline.closeConnectorPicker}
        direction="destination"
        onSelect={exportPipeline.handleConnectorSelect}
        preSelectedTables={exportPipeline.exportSelectedTables.length > 0 ? exportPipeline.exportSelectedTables : undefined}
      />

      <PipelineOutputsDrawer
        open={exportPipeline.outputsDrawerOpen}
        onClose={exportPipeline.closeOutputsDrawer}
        outputs={exportPipeline.completedOutputs}
        sessionId={sessionId}
        onExportSelected={exportPipeline.handleExportSelectedOutputs}
        validationResults={exportPipeline.exportValidationResults}
        connectorName={sessionMeta?.destination_connector_name}
        qualitySummaries={exportPipeline.qualitySummaries}
        hasOutputStructure={hasOutputStructure}
        onAssembleAndDownload={exportPipeline.handleAssembleAndDownload}
        assembling={exportPipeline.assembling}
        validationError={exportPipeline.validationError}
        onClearValidationError={exportPipeline.clearValidationError}
        initialSelectedRefs={Array.isArray(sessionMeta?.selected_output_refs)
          ? (sessionMeta!.selected_output_refs as string[])
          : undefined}
        onSaveSelection={async (refs) => {
          await updateMeta.mutateAsync({ selected_output_refs: refs });
        }}
      />

      <ExportValidationDialog
        open={exportPipeline.validationDialogOpen}
        onClose={exportPipeline.closeValidationDialog}
        validationResults={exportPipeline.validationDialogResults}
        connectorName={exportPipeline.pendingExportConnector?.name || ''}
        onExportAnyway={exportPipeline.handleValidationExportAnyway}
        qualitySummaries={exportPipeline.qualitySummaries}
      />

      <ConnectorPickerDialog
        open={source.sourceConnectorPickerOpen}
        onClose={source.closeSourceConnectorPicker}
        direction="source"
        onSelect={source.handleSourceConnectorSelect}
      />

      {/* Pipeline version history popover */}
      <Popover
        open={!!versionsAnchorEl}
        anchorEl={versionsAnchorEl}
        onClose={() => setVersionsAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360, maxHeight: 320, overflow: 'auto', p: 1.5 } }}
      >
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, px: 0.5 }}>
          Pipeline Versions ({pipelineVersions.length})
        </Typography>
        <Stack spacing={0.5}>
          {pipelineVersions.map((pv) => (
            <Box
              key={pv.pipeline_id}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1,
                borderRadius: 1, cursor: pv.batch_id && pv.batch_id !== batchId ? 'pointer' : 'default',
                bgcolor: pv.batch_id === batchId ? 'action.selected' : undefined,
                '&:hover': { bgcolor: 'action.hover' },
              }}
              onClick={() => {
                if (pv.batch_id && pv.batch_id !== batchId) {
                  setVersionsAnchorEl(null);
                  navigate(`/batches/${pv.batch_id}/workspace`);
                }
              }}
            >
              <Chip
                label={`v${pv.version}`}
                size="small"
                color={pv.batch_id === batchId ? 'primary' : 'default'}
                variant={pv.batch_id === batchId ? 'filled' : 'outlined'}
                sx={{ fontSize: 11, height: 22, fontWeight: 600 }}
              />
              <Typography variant="body2" sx={{ fontSize: 12, flex: 1 }}>
                {pv.batch_name || `Batch ${(pv.batch_id || '').slice(0, 8)}`}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
                {pv.step_count} step{pv.step_count !== 1 ? 's' : ''}
              </Typography>
              <Chip
                label={pv.status}
                size="small"
                color={pv.status === 'completed' ? 'success' : pv.status === 'draft' ? 'default' : 'warning'}
                variant="outlined"
                sx={{ fontSize: 10, height: 20 }}
              />
            </Box>
          ))}
        </Stack>
      </Popover>

      {/* Cross-batch pipeline reuse dialogs */}
      {session?.project_id && (
        <PipelinePickerDialog
          open={pipelinePickerOpen}
          onClose={() => setPipelinePickerOpen(false)}
          onSelect={async (pipelineId) => {
            setPipelinePickerOpen(false);
            // Virtual pipeline from session operations — materialize first
            if (pipelineId.startsWith('session:')) {
              const sid = pipelineId.slice('session:'.length);
              try {
                const pipeline = await materializePipeline(sid);
                setSelectedReusePipelineId(pipeline.pipeline_id);
              } catch {
                // If materialization fails, the version dialog will show an error
                setSelectedReusePipelineId(pipelineId);
              }
            } else {
              setSelectedReusePipelineId(pipelineId);
            }
            setVersionDialogOpen(true);
          }}
          projectId={session.project_id}
          excludeBatchId={batchId}
        />
      )}

      <PipelineNewVersionDialog
        open={versionDialogOpen}
        onClose={() => {
          setVersionDialogOpen(false);
          setSelectedReusePipelineId(undefined);
          // Force immediate refresh so canvas shows injected operations
          queryClient.invalidateQueries(['agent-operations', sessionId]);
          queryClient.invalidateQueries(['agent-workspace', sessionId]);
        }}
        onPrepare={() => versioning.prepare(selectedReusePipelineId)}
        onCreate={(mapping, srcId) => versioning.create(mapping, srcId || selectedReusePipelineId)}
        preparing={versioning.preparing}
        creating={versioning.creating}
      />

      {/* Multi-source: Add Source Dialog (project-level).
          Always rendered (not gated by session?.project_id) so that clicking
          "Upload File" or "From Connector" before the session has fully loaded
          still mounts the dialog. The dialog remains closed (open=false) until
          both addSourceDialogOpen is set AND projectId is available. */}
      <AddSourceDialog
        open={source.addSourceDialogOpen && !!session?.project_id}
        onClose={source.handleCloseAddSourceDialog}
        projectId={session?.project_id ?? ''}
        sessionId={sessionId}
        onSourceAdded={source.handleProjectSourceAdded}
        onUploadsComplete={source.handleUploadsComplete}
        initialTab={source.addSourceDialogInitialTab}
        replaceTable={source.replaceTableName}
        replaceTableGroup={source.replaceTableGroup}
      />

      <input
        type="file"
        ref={mappingGuideInputRef}
        style={{ display: 'none' }}
        accept=".xlsx,.xls"
        onChange={handleUploadMappingGuide}
      />


      {/* Command Palette */}
      <CommandPalette
        open={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        onAddNode={nodeSelection.handleAddNode}
        canAddNode={nodeSelection.canAddNode}
        initialFilter={commandPaletteFilter}
      />

      {/* Pipeline Canvas Context Menu */}
      <PipelineContextMenu
        position={contextMenuPosition}
        onClose={() => setContextMenuPosition(null)}
        onAddNode={nodeSelection.handleAddNode}
        canAddNode={nodeSelection.canAddNode}
      />

      {/* Mapping Guide Preview Modal */}
      <MappingGuidePreviewModal
        open={previewModalOpen}
        preview={previewData}
        onApply={handleApplyPreview}
        onCancel={handleCancelPreview}
        progressText={pipelineProgress?.logLine || 'Uploading and parsing mapping guide...'}
      />

    </Box>
  );
};

export default UnifiedCanvasLayout;
